import React, { useEffect } from 'react';
import Header from './Header';
import Footer from './Footer';
import WhatsAppFloat from './WhatsAppFloat';
import CookieBanner from './CookieBanner';
import StickyMobileCTA from './StickyMobileCTA';
import AnalyticsBeacon from '../AnalyticsBeacon';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * PageShell — shared chrome for every standalone marketing page.
 *
 * Provides: Header, Footer, WhatsAppFloat, CookieBanner, StickyMobileCTA,
 * AnalyticsBeacon and a consistent document.title.
 *
 * Props:
 *  - title    : string  → sets document.title (do not include "Prime Vision")
 *  - testId   : string  → top-level wrapper data-testid
 *  - children : node    → page-specific content (between Header & Footer)
 */
export const PageShell = ({ title, testId, children }) => {
  const { locale } = useLanguage();

  useEffect(() => {
    if (title) document.title = `${title} · Prime Vision`;
  }, [title]);

  // Force scroll to top whenever a standalone page mounts. This keeps
  // navigation feeling "page-like" rather than scroll-restored mid-content.
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
    }
  }, []);

  return (
    <main
      data-testid={testId}
      className="relative bg-white text-pv-ink min-h-screen overflow-x-hidden"
    >
      <Header />
      {children}
      <Footer />
      <WhatsAppFloat />
      <StickyMobileCTA />
      <CookieBanner />
      <AnalyticsBeacon locale={locale} />
    </main>
  );
};

export default PageShell;
